import { useEffect, type ReactElement } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { useQuery } from "@connectrpc/connect-query";
import { Loader2Icon } from "lucide-react";

import { AppShell } from "@/components/app-shell";
import { ErrorState } from "@/components/error-state";
import { AdminService } from "@/gen/wabms/admin/v1/admin_pb";
import { isUnauthenticated } from "@/lib/errors";
import { toDate } from "@/lib/format";
import { AccountDetailPage } from "@/routes/accounts/account-detail-page";
import { AccountsPage } from "@/routes/accounts/accounts-page";
import { BatchDetailPage } from "@/routes/batches/batch-detail-page";
import { BatchesPage } from "@/routes/batches/batches-page";
import { LoginPage } from "@/routes/login-page";
import { NotFoundPage } from "@/routes/not-found-page";
import { SessionsPage } from "@/routes/sessions/sessions-page";
import { WebhooksPage } from "@/routes/webhooks/webhooks-page";
import { useUiStore } from "@/stores/ui-store";

/**
 * The admin session lives in an HttpOnly cookie, so this guard is only a UX
 * shortcut. The server rejects unauthenticated Connect calls regardless, and
 * the query client flips this flag when one of them comes back UNAUTHENTICATED
 * (see lib/transport.ts), which is what makes an expired session bounce here
 * instead of leaving a shell full of failed reads on screen.
 */
function RequireSession({ children }: { children: ReactElement }): ReactElement {
  const authenticated = useUiStore((s) => s.authenticated);
  const location = useLocation();

  if (!authenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return children;
}

function BootSplash() {
  return (
    <div className="flex min-h-svh items-center justify-center bg-background">
      <span className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" /> Checking session…
      </span>
    </div>
  );
}

export function App() {
  const setAuthenticated = useUiStore((s) => s.setAuthenticated);

  // WhoAmI is the single source of truth for "is this browser signed in".
  // Everything below it waits for the answer so the first render never shows
  // the shell to someone whose cookie has already expired.
  const whoAmI = useQuery(
    AdminService.method.whoAmI,
    {},
    { retry: false, staleTime: 30_000, refetchOnWindowFocus: false },
  );

  const { data, error, isError } = whoAmI;

  useEffect(() => {
    if (data) {
      setAuthenticated(data.authenticated, toDate(data.expiresAt));
      return;
    }
    if (isError && isUnauthenticated(error)) setAuthenticated(false);
  }, [data, isError, error, setAuthenticated]);

  if (whoAmI.isPending) return <BootSplash />;

  // A transport-level failure is not the same as "signed out". Say so rather
  // than dumping the operator on a login form that cannot possibly work.
  if (isError && !isUnauthenticated(error)) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background p-6">
        <div className="w-full max-w-lg">
          <ErrorState
            error={error}
            title="Cannot reach the service"
            onRetry={() => void whoAmI.refetch()}
          />
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        element={
          <RequireSession>
            <AppShell />
          </RequireSession>
        }
      >
        <Route index element={<Navigate to="/accounts" replace />} />
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/accounts/:accountId" element={<AccountDetailPage />} />
        <Route path="/batches" element={<BatchesPage />} />
        <Route path="/batches/:batchId" element={<BatchDetailPage />} />
        <Route path="/webhooks" element={<WebhooksPage />} />
        <Route path="/sessions" element={<SessionsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
